import { useEffect, useMemo, useState } from "react";
import { PageHeader, Section } from "@/components/layout/Section";
import { Search, PlayCircle, Download, Headphones, Layers } from "lucide-react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { IMAGES } from "@/constants/branding";

export default function Sermons() {
  const [items, setItems] = useState([]);
  const [seriesMeta, setSeriesMeta] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [activeSeries, setActiveSeries] = useState("All");

  useEffect(() => {
    Promise.all([
      api.get("/sermons", { params: { limit: 100 } }),
      api.get("/youtube/videos", { params: { limit: 30 } }).catch(() => ({ data: { videos: [] } })),
      api.get("/series").catch(() => ({ data: [] })),
    ]).then(([sResp, yResp, mResp]) => {
      const admin = sResp.data || [];
      const yt = (yResp.data?.videos || []).map((v) => ({
        id: `yt-${v.youtube_id}`,
        title: v.title,
        preacher: "Christ Love Breed Church",
        date: v.published || new Date().toISOString(),
        scripture: null,
        description: v.description || "Watch this message from the CLBC YouTube channel.",
        youtube_id: v.youtube_id,
        thumbnail: v.thumbnail,
        series: "CLBC YouTube",
        source: "youtube",
      }));
      const seen = new Set(admin.map((s) => s.youtube_id).filter(Boolean));
      const merged = [...admin, ...yt.filter((v) => !seen.has(v.youtube_id))];
      merged.sort((a, b) => (a.date < b.date ? 1 : -1));
      setItems(merged);
      setSelected(merged[0] || null);
      setSeriesMeta(mResp.data || []);
    });
  }, []);

  const seriesMetaByName = useMemo(() => {
    const m = {};
    (seriesMeta || []).forEach((s) => (m[s.name] = s));
    return m;
  }, [seriesMeta]);

  const seriesList = useMemo(() => {
    const map = new Map();
    items.forEach((s) => {
      const key = s.series && s.series.trim() ? s.series : "Standalone";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return [{ name: "All", count: items.length }, ...Array.from(map, ([name, count]) => ({ name, count }))];
  }, [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((s) => {
      const seriesKey = s.series && s.series.trim() ? s.series : "Standalone";
      if (activeSeries !== "All" && seriesKey !== activeSeries) return false;
      if (!term) return true;
      return (
        s.title.toLowerCase().includes(term) ||
        (s.description || "").toLowerCase().includes(term) ||
        (s.scripture || "").toLowerCase().includes(term) ||
        (s.series || "").toLowerCase().includes(term)
      );
    });
  }, [items, q, activeSeries]);

  const grouped = useMemo(() => {
    const g = new Map();
    filtered.forEach((s) => {
      const key = s.series && s.series.trim() ? s.series : "Standalone";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(s);
    });
    return Array.from(g, ([series, list]) => ({ series, list }));
  }, [filtered]);

  return (
    <div data-testid="sermons-page">
      <PageHeader
        kicker="Sermon Library"
        title="Watch. Listen. Grow."
        subtitle="Search our full library of teachings by title, scripture, or series — or binge an entire series from start to finish."
      />

      <Section>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-10">
          <div className="relative max-w-xl w-full">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="sermons-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sermons, scripture, or series..."
              className="w-full pl-11 pr-4 py-4 rounded-full border border-slate-200 focus:border-royal focus:ring-2 focus:ring-royal/10 outline-none"
            />
          </div>
        </div>

        {/* Series tabs */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="sermon-series-tabs">
          {seriesList.map((s) => (
            <button
              key={s.name}
              onClick={() => setActiveSeries(s.name)}
              data-testid={`series-${s.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeSeries === s.name
                  ? "bg-royal text-white shadow"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-gold hover:text-royal"
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              {s.name}
              <span className={`text-[10px] rounded-full px-2 py-0.5 ${activeSeries === s.name ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                {s.count}
              </span>
            </button>
          ))}
        </div>

        {selected && (
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="lg:col-span-2 card-soft overflow-hidden" data-testid="sermon-player">
              <div className="aspect-video bg-black">
                {selected.youtube_id ? (
                  <iframe
                    key={selected.id}
                    src={`https://www.youtube.com/embed/${selected.youtube_id}`}
                    title={selected.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img src={IMAGES.bible} alt="" className="w-full h-full object-cover opacity-70" />
                )}
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-gold-500">{selected.series || "Sermon"}</div>
                <h2 className="font-serif text-3xl text-royal mt-2">{selected.title}</h2>
                {selected.scripture && <p className="mt-3 scripture">"{selected.scripture}"</p>}
                <p className="mt-4 text-slate-600 leading-relaxed">{selected.description}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {selected.audio_url && (
                    <a href={selected.audio_url} className="btn-secondary text-sm py-2.5" target="_blank" rel="noreferrer">
                      <Headphones className="w-4 h-4" /> Listen
                    </a>
                  )}
                  {selected.notes_url && (
                    <a href={selected.notes_url} className="btn-ghost" target="_blank" rel="noreferrer">
                      <Download className="w-4 h-4" /> Notes
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {filtered.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setSelected(s)}
                  data-testid={`sermon-item-${i}`}
                  className={`w-full text-left card-soft flex gap-4 p-4 items-center ${
                    selected?.id === s.id ? "ring-2 ring-gold" : ""
                  }`}
                >
                  <div className="w-24 aspect-video bg-royal-800 rounded-xl overflow-hidden flex-shrink-0">
                    {s.youtube_id ? (
                      <img
                        src={`https://img.youtube.com/vi/${s.youtube_id}/mqdefault.jpg`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={IMAGES.bible} alt="" className="w-full h-full object-cover opacity-60" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-gold-500 truncate">{s.series}</div>
                    <div className="font-serif text-royal text-base mt-0.5 line-clamp-2">{s.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{new Date(s.date).toLocaleDateString()}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Grouped by series (playlists — Netflix-style rows) */}
        {activeSeries === "All" ? (
          <div className="space-y-16" data-testid="sermon-playlists">
            {grouped.map(({ series, list }) => {
              const meta = seriesMetaByName[series];
              return (
                <div key={series}>
                  <SeriesBanner
                    name={series}
                    tagline={meta?.tagline}
                    description={meta?.description}
                    cover={meta?.cover_image}
                    count={list.length}
                    onViewAll={() => setActiveSeries(series)}
                  />
                  <div className="mt-6">
                    <SermonGrid items={list.slice(0, 6)} setSelected={setSelected} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <SeriesBanner
              name={activeSeries}
              tagline={seriesMetaByName[activeSeries]?.tagline}
              description={seriesMetaByName[activeSeries]?.description}
              cover={seriesMetaByName[activeSeries]?.cover_image}
              count={filtered.length}
            />
            <div className="mt-8">
              <SermonGrid items={filtered} setSelected={setSelected} />
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function SeriesBanner({ name, tagline, description, cover, count, onViewAll }) {
  return (
    <div className="relative rounded-[2rem] overflow-hidden shadow-xl" data-testid={`series-banner-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <div className="aspect-[16/6] sm:aspect-[16/5] relative">
        {cover ? (
          <img src={cover} alt={name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-royal-700 via-royal to-royal-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-royal-800 via-royal-800/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-6 sm:p-10 text-white max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold font-semibold">
            Series · {count} {count === 1 ? "message" : "messages"}
          </div>
          <h3 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight">{name}</h3>
          {tagline && <div className="mt-2 scripture text-white/90">"{tagline}"</div>}
          {description && (
            <p className="mt-3 text-white/70 text-sm leading-relaxed hidden sm:block">{description}</p>
          )}
          {onViewAll && count > 6 && (
            <button onClick={onViewAll} className="mt-5 self-start inline-flex items-center gap-2 text-sm text-gold hover:text-white transition-colors">
              View all in this series →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SermonGrid({ items, setSelected }) {
  if (!items.length) return <div className="text-slate-500 py-8">No sermons in this series yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((s, i) => (
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.04 }}
          key={s.id}
          onClick={() => {
            setSelected(s);
            window.scrollTo({ top: 300, behavior: "smooth" });
          }}
          className="card-soft text-left overflow-hidden"
          data-testid={`sermon-grid-${i}`}
        >
          <div className="aspect-video relative overflow-hidden">
            {s.youtube_id ? (
              <img
                src={s.thumbnail || `https://img.youtube.com/vi/${s.youtube_id}/hqdefault.jpg`}
                alt={s.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <img src={IMAGES.bible} alt="" className="w-full h-full object-cover opacity-80" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-royal-800/60 to-transparent" />
            <div className="absolute bottom-3 left-3 w-10 h-10 rounded-full bg-gold text-royal-800 flex items-center justify-center">
              <PlayCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-gold-500">{s.series}</div>
            <div className="mt-1 font-serif text-lg text-royal line-clamp-2">{s.title}</div>
            <div className="mt-2 text-xs text-slate-500">{new Date(s.date).toLocaleDateString()}</div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}
